import { useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { getStripeEnvironment } from "@/lib/stripe";
import { toast } from "@/components/ui/sonner";

type Sub = { stripe_subscription_id: string; price_id: string; status: string; current_period_end: string | null; cancel_at_period_end: boolean };
type Ent = { price_id: string; created_at: string };

const LABEL: Record<string, string> = {
  makechange_circle_monthly: "MAKECHANGE Circle (monthly)",
  executive_coaching_1on1_monthly: "1:1 Executive Coaching",
  built_on_purpose_masterclass_onetime: "Built On Purpose Masterclass",
  book_a_session_onetime: "Book a Session with Dr. Sully",
  keynote_deposit_onetime: "Keynote Deposit",
};

export default function Members() {
  const { user, loading, signOut } = useAuth();
  const [subs, setSubs] = useState<Sub[]>([]);
  const [ents, setEnts] = useState<Ent[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    const env = (() => { try { return getStripeEnvironment(); } catch { return "sandbox" as const; } })();
    (async () => {
      const [{ data: s }, { data: e }] = await Promise.all([
        supabase.from("subscriptions").select("stripe_subscription_id, price_id, status, current_period_end, cancel_at_period_end").eq("user_id", user.id).eq("environment", env).order("created_at", { ascending: false }),
        supabase.from("entitlements").select("price_id, created_at").eq("user_id", user.id).eq("environment", env).order("created_at", { ascending: false }),
      ]);
      setSubs((s as Sub[]) ?? []);
      setEnts((e as Ent[]) ?? []);
    })();
  }, [user]);

  if (loading) return <div className="min-h-screen bg-navy-deep text-white p-8">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;

  const openPortal = async () => {
    setBusy(true);
    try {
      const env = getStripeEnvironment();
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { environment: env, returnUrl: `${window.location.origin}/members` },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch (err) { toast.error((err as Error).message); }
    finally { setBusy(false); }
  };

  const activeSubs = subs.filter((s) => s.status === "active" || s.status === "trialing" || (s.status === "canceled" && s.current_period_end && new Date(s.current_period_end) > new Date()));
  const hasAnyAccess = activeSubs.length > 0 || ents.length > 0;

  return (
    <div className="min-h-screen bg-navy-deep text-white">
      <PageMeta path="/members" title="My Membership" description="Manage your subscription and access your purchases." noindex />
      <SiteNav />
      <main className="container max-w-4xl py-16">
        <div className="flex items-baseline justify-between mb-8">
          <div>
            <span className="eyebrow">Members</span>
            <h1 className="font-display font-bold uppercase text-3xl md:text-4xl mt-2">Welcome{user.email ? `, ${user.email}` : ""}</h1>
          </div>
          <button onClick={signOut} className="text-sm text-silver underline">Sign out</button>
        </div>

        <section className="rounded-xl border border-border bg-navy/40 p-6 mb-6">
          <h2 className="font-display uppercase tracking-wide mb-4">Subscriptions</h2>
          {activeSubs.length === 0 ? (
            <p className="text-silver text-sm">No active subscription. <Link className="underline text-gold" to="/book?price=makechange_circle_monthly">Join the Circle →</Link></p>
          ) : (
            <ul className="space-y-3">
              {activeSubs.map((s) => (
                <li key={s.stripe_subscription_id} className="flex items-center justify-between border-b border-border/60 pb-3">
                  <div>
                    <div className="font-medium">{LABEL[s.price_id] ?? s.price_id}</div>
                    <div className="text-xs text-silver">
                      Status: {s.status}
                      {s.cancel_at_period_end && s.current_period_end ? ` · Access until ${new Date(s.current_period_end).toLocaleDateString()}` : ""}
                      {!s.cancel_at_period_end && s.current_period_end ? ` · Renews ${new Date(s.current_period_end).toLocaleDateString()}` : ""}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
          {activeSubs.length > 0 && (
            <Button disabled={busy} onClick={openPortal} className="mt-4 bg-cta text-white font-display uppercase tracking-[0.2em] text-xs px-5 py-4 shadow-cta">
              {busy ? "…" : "Manage billing"}
            </Button>
          )}
        </section>

        <section className="rounded-xl border border-border bg-navy/40 p-6 mb-6">
          <h2 className="font-display uppercase tracking-wide mb-4">Purchases</h2>
          {ents.length === 0 ? (
            <p className="text-silver text-sm">No purchases yet.</p>
          ) : (
            <ul className="space-y-2">
              {ents.map((e, i) => (
                <li key={i} className="flex items-center justify-between text-sm">
                  <span>{LABEL[e.price_id] ?? e.price_id}</span>
                  <span className="text-silver text-xs">{new Date(e.created_at).toLocaleDateString()}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        {hasAnyAccess && (
          <section className="rounded-xl border border-gold/40 bg-gold/5 p-6">
            <h2 className="font-display uppercase tracking-wide text-gold mb-2">Member resources</h2>
            <p className="text-silver text-sm mb-3">Your gated content, replays, and worksheets live here.</p>
            <Link to="/framework" className="underline text-gold">Open the MAKECHANGE framework →</Link>
          </section>
        )}
      </main>
      <SiteFooter />
    </div>
  );
}
